import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Activity, Ban, CheckCircle2, Copy, Settings2, Save, X, Trophy, Flame } from "lucide-react";
import api, { formatApiErrorDetail, safeCopy } from "@/lib/api";

const mono = "font-mono text-[10px] tracking-[0.2em] uppercase";
const inputCls = "bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

function StatusChip({ children, cls }) {
  return <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-1 ${cls}`}>{children}</span>;
}

export default function AdminSalesTeam() {
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [reps, setReps] = useState([]);
  const [company, setCompany] = useState(null);
  const [activity, setActivity] = useState(null); // {rep, entries}
  const [approvedPw, setApprovedPw] = useState(null); // {email, temp_password}
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [bonus, setBonus] = useState(null);
  const [bonusMonth, setBonusMonth] = useState("");
  const [teamKpi, setTeamKpi] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/sales/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/sales/reps").then((r) => setReps(r.data)).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const loadBonus = useCallback((m = "") => {
    api.get(`/admin/sales/bonus/preview${m ? `?month=${m}` : ""}`).then((r) => { setBonus(r.data); setBonusMonth(r.data.month); }).catch(() => {});
  }, []);

  const loadTeamKpi = useCallback(() => {
    api.get("/sales/kpi/team?days=7").then((r) => setTeamKpi(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadBonus(); loadTeamKpi(); }, [load, loadBonus, loadTeamKpi]);

  const awardBonus = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/sales/bonus/monthly", { month: bonusMonth });
      toast.success(r.data.message);
      loadBonus(bonusMonth);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (app) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sales/applications/${app.id}/approve`);
      setApprovedPw({ email: app.email, temp_password: r.data.temp_password });
      toast.success("Application approved — sales account ready");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/sales/applications/${declineFor.id}/decline`, { reason });
      toast.success("Application declined");
      setDeclineFor(null); setReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openActivity = async (rep) => {
    try {
      const r = await api.get(`/admin/sales/reps/${rep.id}/activity`);
      setActivity({ rep, entries: r.data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleSuspend = async (rep) => {
    const action = rep.suspended ? "reinstate" : "suspend";
    try {
      const r = await api.post(`/admin/sales/reps/${rep.id}/${action}`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveRates = async () => {
    try {
      await api.put("/company", { commission_pct: Number(company.commission_pct), sales_fee_pct: Number(company.sales_fee_pct) });
      toast.success("Program rates saved — applies to new deals");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pending = apps.filter((a) => a.status === "pending");

  return (
    <section data-testid="admin-sales-team" className="flex flex-col gap-6">
      <div className="flex items-center gap-2 border-b border-white/10">
        {[["applications", `Applications (${pending.length})`, UserPlus], ["reps", `Reps (${reps.length})`, Users], ["activity", "Team Activity", Activity], ["bonus", "Monthly Bonus", Trophy], ["settings", "Program Rates", Settings2]].map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`salesteam-tab-${id}`}
            onClick={() => setTab(id)}
            className={`${mono} px-4 py-2.5 flex items-center gap-2 border-b-2 transition-colors ${tab === id ? "border-nc-orange text-nc-orange" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {approvedPw && (
        <div data-testid="approved-credentials" className="border border-nc-green/40 bg-nc-green/5 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="font-sans font-bold uppercase text-sm text-nc-green flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Sales account created</p>
            <button onClick={() => setApprovedPw(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <p className="font-mono text-xs text-zinc-300">Email: <span className="text-zinc-100">{approvedPw.email}</span></p>
          {approvedPw.temp_password ? (
            <div className="flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-2 max-w-md">
              <p data-testid="approved-temp-password" className="font-mono text-sm text-nc-green flex-1">{approvedPw.temp_password}</p>
              <button onClick={async () => { (await safeCopy(approvedPw.temp_password)) ? toast.success("Password copied") : toast.error("Copy blocked"); }} className="text-zinc-400 hover:text-white"><Copy className="w-4 h-4" /></button>
            </div>
          ) : (
            <p className="font-mono text-[10px] text-zinc-500">Existing account promoted to sales — no new password issued.</p>
          )}
          <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em]">Share these credentials with the rep securely — shown once. They must complete the Sales Academy before working leads.</p>
        </div>
      )}

      {tab === "applications" && (
        <div className="flex flex-col gap-3">
          {apps.length === 0 && <p data-testid="applications-empty" className="font-mono text-[10px] text-zinc-600 py-8 text-center">No applications yet — the /join-sales recruitment page feeds this queue.</p>}
          {apps.map((a, i) => (
            <div key={a.id} data-testid={`application-row-${i + 1}`} className="border border-white/10 bg-black/40 p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-sans font-bold text-sm text-zinc-100">{a.name}</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{a.email} // {a.hours} hrs/week // {String(a.created_at).slice(0, 10)}</p>
                </div>
                <StatusChip cls={a.status === "pending" ? "text-nc-orange border-nc-orange/40 bg-nc-orange/10" : a.status === "approved" ? "text-nc-green border-nc-green/40 bg-nc-green/10" : "text-zinc-500 border-white/15"}>{a.status}</StatusChip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 font-mono text-[11px] text-zinc-400 leading-relaxed">
                <div><p className="text-zinc-600 uppercase tracking-[0.2em] text-[9px] mb-1">Experience</p>{a.experience}</div>
                <div><p className="text-zinc-600 uppercase tracking-[0.2em] text-[9px] mb-1">Pitch</p>{a.pitch}</div>
              </div>
              {a.networks && <p className="font-mono text-[10px] text-zinc-500 mt-3">Networks: {a.networks}</p>}
              {a.status === "pending" && (
                <div className="flex items-center gap-2 mt-4">
                  <button data-testid={`application-approve-${i + 1}`} disabled={busy} onClick={() => approve(a)} className="font-mono text-[9px] tracking-[0.2em] uppercase bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-white px-4 py-2 transition-colors">Approve & Create Account</button>
                  <button data-testid={`application-decline-${i + 1}`} disabled={busy} onClick={() => { setDeclineFor(a); setReason(""); }} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-red-400/40 text-red-400 hover:bg-red-400/10 px-4 py-2 transition-colors">Decline</button>
                </div>
              )}
              {declineFor?.id === a.id && (
                <div className="mt-3 flex items-center gap-2">
                  <input data-testid="decline-reason" className={`${inputCls} flex-1`} placeholder="Reason (optional, logged)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <button data-testid="decline-confirm" disabled={busy} onClick={decline} className="font-mono text-[9px] tracking-[0.2em] uppercase bg-red-500 hover:bg-red-500/80 text-white px-4 py-2 transition-colors">Confirm Decline</button>
                  <button onClick={() => setDeclineFor(null)} className="font-mono text-[9px] uppercase text-zinc-500 px-2">Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "reps" && (
        <div className="flex flex-col gap-3">
          {reps.length === 0 && <p data-testid="reps-empty" className="font-mono text-[10px] text-zinc-600 py-8 text-center">No sales reps yet — approve an application to onboard the first rep.</p>}
          {reps.map((r, i) => (
            <div key={r.id} data-testid={`rep-row-${i + 1}`} className={`border bg-black/40 p-5 ${r.suspended ? "border-red-400/40" : "border-white/10"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-sans font-bold text-sm text-zinc-100 flex items-center gap-2">
                    {r.name}
                    {r.suspended && <StatusChip cls="text-red-400 border-red-400/40 bg-red-400/10">Suspended</StatusChip>}
                    {!r.onboarding_done && <StatusChip cls="text-nc-blue border-nc-blue/40 bg-nc-blue/10">Academy {r.onboarding_steps}/{r.onboarding_total}</StatusChip>}
                  </p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{r.email} // {r.recruitment_id} // last: {r.last_action} {r.last_active ? `(${String(r.last_active).slice(0, 16).replace("T", " ")})` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`rep-activity-${i + 1}`} onClick={() => openActivity(r)} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-white/15 text-zinc-300 hover:border-nc-blue hover:text-nc-blue px-3 py-2 transition-colors flex items-center gap-1.5"><Activity className="w-3 h-3" /> Activity</button>
                  <button data-testid={`rep-suspend-${i + 1}`} onClick={() => toggleSuspend(r)} className={`font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${r.suspended ? "border-nc-green/40 text-nc-green hover:bg-nc-green/10" : "border-red-400/40 text-red-400 hover:bg-red-400/10"}`}><Ban className="w-3 h-3" /> {r.suspended ? "Reinstate" : "Suspend"}</button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4 text-center">
                {[
                  ["Claimed", r.leads_claimed, "text-zinc-200"],
                  ["Closed", r.leads_closed, "text-zinc-200"],
                  ["Conversion", `${r.conversion}%`, "text-nc-blue"],
                  ["Gross", `AUD ${r.gross_earned.toFixed(0)}`, "text-zinc-200"],
                  ["Studio Fee", `AUD ${r.fees_retained.toFixed(0)}`, "text-nc-orange"],
                  ["Rep Net", `AUD ${r.net_earned.toFixed(0)}`, "text-nc-green"],
                ].map(([label, value, cls]) => (
                  <div key={label} className="border border-white/10 px-2 py-2">
                    <p className={`font-sans font-bold text-sm ${cls}`}>{value}</p>
                    <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em] mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
              {activity?.rep.id === r.id && (
                <div data-testid="rep-activity-feed" className="mt-4 border border-white/10 bg-black/60 max-h-64 overflow-y-auto">
                  <div className="border-b border-white/10 px-4 py-2 flex items-center justify-between">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">Audit trail — {r.email}</p>
                    <button onClick={() => setActivity(null)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {activity.entries.length === 0 && <p className="font-mono text-[10px] text-zinc-600 p-4">No recorded actions yet.</p>}
                  {activity.entries.map((e) => (
                    <div key={e.id} className="px-4 py-2 border-b border-white/5 font-mono text-[10px] flex items-center gap-3 flex-wrap">
                      <span className="text-zinc-600 shrink-0">{String(e.at).slice(0, 16).replace("T", " ")}</span>
                      <span className="text-nc-blue shrink-0">{e.action}</span>
                      <span className="text-zinc-400 truncate">{e.target} {e.detail && `// ${e.detail}`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "activity" && (
        <div data-testid="salesteam-activity" className="border border-white/10 bg-black/40">
          <div className="border-b border-white/10 px-5 py-3.5 flex items-center justify-between">
            <p className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100 flex items-center gap-2"><Activity className="w-4 h-4 text-nc-blue" /> Team Activity — Last 7 Days</p>
            <button data-testid="team-kpi-refresh" onClick={loadTeamKpi} className={`${mono} text-zinc-400 hover:text-white transition-colors`}>Refresh</button>
          </div>
          {!teamKpi || teamKpi.reps.length === 0 ? (
            <p data-testid="team-kpi-empty" className="font-mono text-[10px] text-zinc-600 p-5">No activity logged yet — reps log daily numbers from the KPI tracker on their dashboard.</p>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                <div className="grid grid-cols-[1fr_90px_90px_90px_90px_90px_90px] border-b border-white/10 px-5 py-2.5 bg-white/5">
                  {["Rep", "Outreach", "Links", "Posts", "Chats", "Scopes", "Days active"].map((h) => <span key={h} className={mono}>{h}</span>)}
                </div>
                {teamKpi.reps.map((r, i) => (
                  <div key={r.rep_id} data-testid={`team-kpi-row-${i + 1}`} className="grid grid-cols-[1fr_90px_90px_90px_90px_90px_90px] items-center border-b border-white/5 px-5 py-3">
                    <span className="font-mono text-[11px] text-zinc-200 truncate">{r.email}</span>
                    <span className={`font-mono text-[11px] ${r.outreach_dms >= 140 ? "text-nc-orange font-bold" : "text-zinc-300"}`}>{r.outreach_dms}</span>
                    <span className="font-mono text-[11px] text-zinc-300">{r.links_shared}</span>
                    <span className="font-mono text-[11px] text-zinc-300">{r.posts_audited}</span>
                    <span className="font-mono text-[11px] text-zinc-300">{r.active_chats}</span>
                    <span className="font-mono text-[11px] text-zinc-300">{r.scopes_drafted}</span>
                    <span className={`font-mono text-[11px] flex items-center gap-1 ${r.days_active >= 5 ? "text-nc-orange" : "text-zinc-400"}`}><Flame className="w-3 h-3" />{r.days_active}/7</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="font-mono text-[8px] text-zinc-600 px-5 py-3">Orange = gold-tier pace (140+ outreach/week ≈ 20/day). Targets from Master Guide Section 06.</p>
        </div>
      )}

      {tab === "bonus" && (
        <div data-testid="salesteam-bonus" className="border border-white/10 bg-black/40">
          <div className="border-b border-white/10 px-5 py-3.5">
            <p className="font-sans font-semibold uppercase tracking-widest text-sm text-zinc-100 flex items-center gap-2"><Trophy className="w-4 h-4 text-nc-orange" /> Monthly Top Commission Bonus — AUD 500</p>
          </div>
          <div className="p-5 flex flex-col gap-4 max-w-2xl">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Bonus month</label>
                <input data-testid="bonus-month-input" type="month" className={inputCls} value={bonusMonth} onChange={(e) => { setBonusMonth(e.target.value); loadBonus(e.target.value); }} />
              </div>
              <p className="font-mono text-[9px] text-zinc-600 pb-2">Defaults to last month — bonus ranks by net commission earned in that month.</p>
            </div>
            {bonus?.awarded ? (
              <div data-testid="bonus-awarded" className="border border-nc-green/40 bg-nc-green/5 p-4">
                <p className="font-mono text-[10px] text-nc-green uppercase tracking-[0.2em] flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Already awarded for {bonus.month}</p>
                <p className="font-mono text-[11px] text-zinc-300 mt-2">{bonus.awarded.note} — claimable in the rep's earnings panel.</p>
              </div>
            ) : bonus?.leader ? (
              <div className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-3">
                <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Projected winner — {bonus.month}</p>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p data-testid="bonus-leader-name" className="font-sans font-bold text-base text-zinc-50">{bonus.leader.name}</p>
                    <p className="font-mono text-[10px] text-zinc-400 mt-0.5">AUD {bonus.leader.net} net // {bonus.leader.deals} deal(s)</p>
                  </div>
                  <button data-testid="bonus-award-button" disabled={busy} onClick={awardBonus} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center gap-2 transition-colors">
                    <Trophy className="w-3.5 h-3.5" /> {busy ? "Awarding…" : "Award AUD 500 Bonus"}
                  </button>
                </div>
              </div>
            ) : (
              <p data-testid="bonus-no-leader" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-4">No closed commissions in {bonusMonth} — no bonus due.</p>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && company && (
        <div data-testid="program-rates" className="border border-white/10 bg-black/40 p-6 max-w-xl flex flex-col gap-5">
          <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase leading-relaxed">
            Rates apply to newly closed deals. Rep net = gross commission minus the Specpixel platform fee.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`${mono} text-zinc-500 block mb-1.5`}>Rep commission % (of project)</label>
              <input data-testid="rate-commission" type="number" min="0" max="100" className={`${inputCls} w-full`} value={company.commission_pct} onChange={(e) => setCompany((c) => ({ ...c, commission_pct: e.target.value }))} />
            </div>
            <div>
              <label className={`${mono} text-zinc-500 block mb-1.5`}>Specpixel fee % (of commission)</label>
              <input data-testid="rate-fee" type="number" min="0" max="100" className={`${inputCls} w-full`} value={company.sales_fee_pct} onChange={(e) => setCompany((c) => ({ ...c, sales_fee_pct: e.target.value }))} />
            </div>
          </div>
          <p className="font-mono text-[10px] text-zinc-500">
            Example: AUD 8,000 project → gross AUD {(8000 * Number(company.commission_pct || 0) / 100).toFixed(2)} → fee AUD {(8000 * Number(company.commission_pct || 0) / 100 * Number(company.sales_fee_pct || 0) / 100).toFixed(2)} → <span className="text-nc-green">rep nets AUD {(8000 * Number(company.commission_pct || 0) / 100 * (1 - Number(company.sales_fee_pct || 0) / 100)).toFixed(2)}</span>
          </p>
          <button data-testid="rates-save" onClick={saveRates} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-5 py-2.5 flex items-center gap-2 transition-colors"><Save className="w-3.5 h-3.5" /> Save Rates</button>
        </div>
      )}
    </section>
  );
}
